/**
 * menuComposition.js — 관리자 메뉴 조립 한 곳 (2026-09-15)
 *
 * 사이드바(SidebarContent)와 홈 기능 카드(FeatureLauncher)가 **같은 메뉴**를 보여줘야 하는데, 조립 로직이
 * 두 파일에 따로 복사돼 있었다. 그래서 "운영자가 고객 테넌트를 고르면 그 테넌트 관리자와 같은 메뉴" 수정이
 * 사이드바에만 들어가고 홈 카드에는 빠졌다(2026-09-15 발견). 이제 둘 다 이 함수를 쓴다.
 *
 * 분류는 DB(saas_modules.category → menuCategories.js). 권한 필터·회사 선택 규칙은 종전 그대로다.
 * 직원 화면을 골라 보던 "…열람" 줄(drill-down)은 2026-09-15 사용자 요청으로 없앴다 — 사이드바·홈 카드 모두.
 */
import { MENU_GROUPS, MY_MENUS, buildExtCallMenus, simplifySoleExtCallLabel } from './menuConfig';
import { filterMyMenus, filterMenuGroups } from './sidebarFilters';
import {
  buildCompanyItems, groupCompanyItems, GLOBAL_GROUP_NAMES, buildCustomerTenantGroups,
} from './companyMenuSet';
import { regroupMenuGroups, categoryOfPath } from './menuCategories';
import { nameCallMenus, CALL_ALL_PATH } from './callMenuNaming';

// A '(통합)' 칸 빼기 — 일일콜 켠 회사 2곳 이상·모름이면(2026-10-14, 통합 계획 3단계 7번).
//   같은 내용은 작업 대상 → 통합관리의 통합 일일콜 메뉴로 옮겼다. 빈 그룹은 지운다.
function dropCallAll(groups) {
  return groups
    .map((g) => ({ ...g, items: (g.items || []).filter((it) => it.to !== CALL_ALL_PATH) }))
    .filter((g) => g.items.length > 0);
}

// 고객 테넌트 보기: 일반 메뉴(권한) + 그 테넌트 관리자가 **자기 회사 메뉴로 받는 회사별 기능**(급여·계약취소·매출 …).
//   테넌트 관리자 사이드바의 own 병합과 같은 규칙(buildCompanyItems mode 'own')이다. 회사별 기능이 빠져 있어서
//   예산에 급여 관리를 켜도 운영자 화면에만 안 나오던 빈틈을 막는다(2026-09-15).
function customerTenantGroups({ customerView, tenantPerms, menuGroups, groupOrder, followCategory }) {
  const base = buildCustomerTenantGroups(customerView.companyCode, tenantPerms, menuGroups);
  const cm = tenantPerms ? customerView.companyMenu : null;
  if (!cm) return base;
  const seen = new Set(base.flatMap((g) => g.items.map((it) => it.to)));
  const own = followCategory(buildCompanyItems(cm, { permCodes: tenantPerms, mode: 'own' })).filter((it) => !seen.has(it.to));
  if (own.length === 0) return base;
  return groupCompanyItems([...base.flatMap((g) => g.items.map((it) => ({ ...it, group: g.group }))), ...own], groupOrder);
}

/**
 * @param {object} p
 * @param {object|null} p.cats        DB 분류(useMenuCategories). null 이면 코드 분류.
 * @param {object} p.permissions      usePermissions 결과(canUse* boolean)
 * @param {Set<string>} p.permCodes   보는 사람 권한
 * @param {boolean} p.isAdmin
 * @param {object|null} p.scope       useCompanyScope()
 * @returns {{ menuGroups, myMenus, hideMyMenus, visibleGroups, groupOrder }}
 */
export function composeAdminMenus({ cats, permissions, permCodes, isAdmin, scope }) {
  const menuGroups = regroupMenuGroups(MENU_GROUPS, cats);
  const groupOrder = menuGroups.map((g) => g.group);
  const selectedCompany = scope?.company || null;
  // 타사(ext) 선택 시: 그 회사의 메뉴 세트 + 전역 그룹만. primary/own 은 일반 그룹(현행).
  const isExtView = selectedCompany?.kind === 'ext';

  // 정적 메뉴(permKey 필터) + 회사별 동적 콜 메뉴(TM_FEATURE_EXT_{CODE}). 콜 메뉴가 그것 하나면 괄호를 뺀다.
  const myMenus = simplifySoleExtCallLabel([...filterMyMenus(MY_MENUS, permissions), ...buildExtCallMenus(permCodes)]);

  // 관리자·테넌트 관리자는 "내 정보"(본인 데이터 메뉴) 구역을 두지 않는다 — 본인 기록이 없는 관리 계정이다.
  const hideMyMenus = isAdmin || Boolean(permCodes && permCodes.has('TENANT_ADMIN'));
  // 운영자가 고객 테넌트를 골랐으면 그 테넌트 관리자와 같은 메뉴. 권한 미확보 → 없음(fail-closed).
  const customerView = scope?.customerView || null;
  const tenantPerms = (customerView?.ready && customerView.isPrimary) ? customerView.permCodes : null;

  const generalGroups = filterMenuGroups(menuGroups, permCodes, isAdmin);
  const globalGroups = generalGroups.filter((g) => GLOBAL_GROUP_NAMES.has(g.group));
  // 회사별 메뉴 세트는 같은 기능의 일반 메뉴가 놓인 분류를 따른다(없으면 표에 적힌 그룹)
  const followCategory = (items) => items.map((it) => ({ ...it, group: categoryOfPath(menuGroups, it.hqPath) || it.group }));

  let visibleGroups;
  if (isExtView) {
    visibleGroups = [
      // 고객 테넌트 = 그 테넌트 관리자와 같은 메뉴 / 운영사 자기 회사(DOT·LNJ 등) = 회사 메뉴 세트(종전)
      ...(customerView
        ? customerTenantGroups({ customerView, tenantPerms, menuGroups, groupOrder, followCategory })
        : groupCompanyItems(followCategory(buildCompanyItems(selectedCompany, { permCodes, mode: 'ext' })), groupOrder)),
      ...globalGroups,
    ];
  } else {
    // own(비운영자 자기 회사): 일반 그룹 + 자기 회사의 ext 잔여 항목을 주제 그룹에 병합 (구 아코디언 평평 모드)
    // A '(통합)' 칸이 없으면 자기 회사 콜 메뉴를 남긴다(2026-10-14 — 전엔 '2곳+ 테넌트의 메인 회사'만).
    //   회사 기능 표의 call 켜짐 검사(companyMenuSet keepCall)가 그대로 걸러 준다.
    const keepOwnCall = !scope?.callAllVisible;
    const ownExtItems = (selectedCompany?.kind === 'own' && selectedCompany.enabled_features)
      ? followCategory(buildCompanyItems(selectedCompany, { permCodes, mode: 'own', keepOwnCall }))
      : [];
    visibleGroups = ownExtItems.length > 0
      ? groupCompanyItems(
        [...generalGroups.flatMap((g) => g.items.map((it) => ({ ...it, group: g.group }))), ...ownExtItems],
        groupOrder,
      )
      : generalGroups;
  }

  // A '(통합)' 칸은 일일콜 켠 회사가 정확히 1곳일 때만(me.callCompanyCount, 모르면 뺀다 — fail-closed).
  if (!scope?.callAllVisible) visibleGroups = dropCallAll(visibleGroups);

  // 일일콜 관리 메뉴 이름: 통합/본사, 회사 하나면 하나만 (사용자 확정 2026-09-24). 이름을 바꾸거나 빼기만 한다.
  visibleGroups = nameCallMenus(visibleGroups, scope?.tenantInfo);

  return { menuGroups, myMenus, hideMyMenus, visibleGroups, groupOrder };
}
